import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit2, Trash2, Plus, X, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCoaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', calendly_link: '', bio: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadCoaches(); }, []);

  const loadCoaches = () => {
    axios.get(`${API}/api/admin/coaches`, { withCredentials: true }).then(r => setCoaches(r.data)).catch(() => {});
  };

  const resetForm = () => {
    setForm({ name: '', email: '', calendly_link: '', bio: '' });
    setShowForm(false);
    setEditId(null);
    setError('');
  };

  const handleEdit = (coach) => {
    setForm({ name: coach.name, email: coach.email, calendly_link: coach.calendly_link || '', bio: coach.bio || '' });
    setEditId(coach.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this coach? They will be unassigned from all clients.')) return;
    try {
      await axios.delete(`${API}/api/admin/coaches/${id}`, { withCredentials: true });
      loadCoaches();
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (editId) {
        await axios.put(`${API}/api/admin/coaches/${editId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/coaches`, form, { withCredentials: true });
      }
      resetForm();
      loadCoaches();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save coach');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-coaches">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 className="portal-page-title">Manage Coaches</h1>
          <p className="portal-page-subtitle" style={{ margin: 0 }}>Add coaches and assign them to clients</p>
        </div>
        <button className="portal-btn portal-btn-primary" onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-coach-btn">
          {showForm && !editId ? <><X size={16} /> Cancel</> : <><Plus size={16} /> Add Coach</>}
        </button>
      </div>

      {showForm && (
        <div className="portal-card" style={{ marginBottom: '1.5rem' }}>
          <h3>{editId ? 'Edit Coach' : 'New Coach'}</h3>
          {error && <div className="portal-login-error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          <form className="portal-form" style={{ marginTop: '1rem' }} onSubmit={handleSubmit} data-testid="coach-form">
            <div className="portal-grid-2">
              <div>
                <label>Full Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="coach-name" />
              </div>
              <div>
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required data-testid="coach-email" />
              </div>
            </div>
            <div>
              <label>Calendly Link *</label>
              <input type="url" value={form.calendly_link} onChange={e => setForm(f => ({ ...f, calendly_link: e.target.value }))} placeholder="https://calendly.com/coach-name" required data-testid="coach-calendly" />
            </div>
            <div>
              <label>Bio (optional)</label>
              <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Brief coach biography..." rows={3} data-testid="coach-bio" />
            </div>
            <button type="submit" className="portal-btn portal-btn-primary" disabled={submitting} data-testid="save-coach-btn">
              {submitting ? 'Saving...' : editId ? <><Save size={16} /> Update Coach</> : <><Plus size={16} /> Add Coach</>}
            </button>
          </form>
        </div>
      )}

      {coaches.length === 0 ? (
        <div className="portal-empty"><p>No coaches yet. Add your first coach to get started.</p></div>
      ) : (
        coaches.map(c => (
          <div key={c.id} className="portal-card" data-testid={`coach-card-${c.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, color: '#1B2A4E' }}>{c.name}</h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.88rem', color: '#5A6C8A' }}>{c.email}</p>
                {c.calendly_link && (
                  <a href={c.calendly_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', color: '#C8963E', textDecoration: 'none', display: 'inline-block', marginTop: '0.25rem' }}>
                    {c.calendly_link}
                  </a>
                )}
                {c.bio && <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#4A5568', lineHeight: 1.5 }}>{c.bio}</p>}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
                <button onClick={() => handleEdit(c)} className="portal-btn portal-btn-outline" style={{ padding: '0.4rem 0.75rem', fontSize: '0.82rem' }} data-testid={`edit-coach-${c.id}`}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(c.id)} className="portal-btn" style={{ padding: '0.4rem 0.75rem', fontSize: '0.82rem', color: '#DC2626', border: '1px solid #DC2626' }} data-testid={`delete-coach-${c.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AdminCoaches;
